/**
 * Native (Android/iOS) app version, targetable inline in the Statsig console.
 *
 * Parsed from the webview User-Agent token `quince_app_<os>_<version>-<otaVersion>` (set by
 * ecomm-hybrid-app's `getUserAgent()`), e.g. `quince_app_ios_1.260317.0-r0` — on the SERVER from the
 * request UA (see `get-app-version-from-user-agent.ts`), on the CLIENT from `navigator.userAgent`.
 * Surfaced as the custom fields `app_version` (store binary version, e.g. `1.260317.0` — use
 * Statsig's `version` comparator) and `app_ota_version` (the OTA JS-bundle revision, e.g. `r0`).
 * Absent for web/mWeb, so nothing is added there.
 */
export interface StatsigAppVersion {
  version?: string
  otaVersion?: string
}

const APP_TOKEN_REGEX = /quince_app_(?:ios|android)_(\S+)/i

/**
 * `quince_app_webview` User-Agent token the app sends on every request, alongside the version
 * token above — the same token the client helper `getIsNativeViewClient`
 * (quince-site-common/helpers) keys on. Owned here (pure + isomorphic, next to its sibling
 * `APP_TOKEN_REGEX`) so server consumers share one definition instead of re-declaring it; the
 * widely-shared client helper keeps its own inline copy untouched — if the token ever changes,
 * update it there too.
 */
export const NATIVE_APP_WEBVIEW_UA_TOKEN = /quince_app_webview/i

/**
 * Extract the app version from a webview User-Agent. Splits the token on its first `-` into the
 * store binary version (`app_version`) and the OTA revision (`app_ota_version`). Returns
 * `undefined` when the UA carries no app token (web/mWeb). Pure + isomorphic — shared by the server
 * (request UA) and client (`navigator.userAgent`) paths so both produce an identical custom shape.
 */
export function parseAppVersionFromUserAgent(userAgent: string | undefined): StatsigAppVersion | undefined {
  const token = userAgent?.match(APP_TOKEN_REGEX)?.[1]
  if (!token) return undefined

  const separatorIndex = token.indexOf('-')
  const version = separatorIndex === -1 ? token : token.slice(0, separatorIndex)
  const otaVersion = separatorIndex === -1 ? '' : token.slice(separatorIndex + 1)

  const appVersion: StatsigAppVersion = {}
  if (version) {
    appVersion.version = version
  }
  if (otaVersion) {
    appVersion.otaVersion = otaVersion
  }
  return appVersion.version === undefined && appVersion.otaVersion === undefined ? undefined : appVersion
}

/**
 * The `app_*` custom fields a Statsig console rule targets with the `version` comparator. Only adds
 * a key when it is actually present, so web/mWeb users (and any request whose UA carries no app
 * token) keep an app-version-free custom map — a `version >=` rule then simply never matches them.
 *
 * Shared by the full request user (`buildStatsigCustom`) and by anonymous gate evaluation, which
 * needs the app version WITHOUT a per-user identity (see `getAppVersionAwareFeatureGateValue`).
 */
export const buildAppVersionCustom = (appVersion?: StatsigAppVersion): Record<string, string> => {
  const appVersionCustom: Record<string, string> = {}
  if (appVersion?.version) {
    appVersionCustom.app_version = appVersion.version
  }
  if (appVersion?.otaVersion) {
    appVersionCustom.app_ota_version = appVersion.otaVersion
  }
  return appVersionCustom
}

export const buildStatsigCustom = ({
  isCountryCompliant,
  forcedBuckets,
  consentCookieValue,
  appVersion,
}: {
  isCountryCompliant: boolean
  forcedBuckets: Record<string, string | string[]>
  consentCookieValue: string | undefined
  appVersion?: StatsigAppVersion
}): Record<string, string | string[] | boolean> => {
  const appVersionCustom = buildAppVersionCustom(appVersion)

  if (!isCountryCompliant) return { ...forcedBuckets, ...appVersionCustom }
  return { ...forcedBuckets, ...appVersionCustom, hasConsented: consentCookieValue === 'true' }
}
